import { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record';
import { MicrophoneIcon, PlayIcon, SendIcon } from 'icons';
import { Button } from 'components/button';
import { useGetTheme } from 'hooks/useGetTheme';
import { CheckIcon, StopIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useBreakpoint } from 'hooks/useBreakPoint';
import { uniqueId } from 'lodash';
import { convertSecondsToTimeWithSecond } from '../../utilities/converts';

interface IRecorder {
    onAudioUrlChanged: (url: string, blob: Blob) => void;
    isFetchingText: boolean;
    isTextFetched: boolean;
    clear: () => void;
}

export function VoiceToText({
    onAudioUrlChanged,
    isFetchingText,
    isTextFetched,
    clear,
}: IRecorder) {
    const wavesurferReady = useRef<boolean>(false);
    const wavesurfer = useRef<WaveSurfer | undefined>(undefined);
    const record = useRef<RecordPlugin | undefined>(undefined);

    const [recording, setRecording] = useState(false);
    const [recorded, setRecorded] = useState(false);
    const [duration, setDuration] = useState('00:00');

    const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
    const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
    const [shouldReset, setShouldReset] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    const id = `waveform-recording-${uniqueId()}`;
    const theme = useGetTheme();

    const createRecorder = () => {
        wavesurfer.current = WaveSurfer.create({
            container: `#${id}`,
            waveColor: '#CDD6DB',
            progressColor: '#05A3E4',
            cursorColor: theme === 'light' ? '#000' : '#fff',
            height: 20,
            autoCenter: false,
        });

        record.current = wavesurfer.current.registerPlugin(
            RecordPlugin.create({
                scrollingWaveform: true,
                renderRecordedAudio: false,
            })
        );

        record.current.on('record-start', () => {
            setRecording(true);
            setRecorded(false);
        });

        record.current.on('record-resume', () => {
            setRecording(true);
            setRecorded(false);
        });

        record.current.on('record-pause', () => {
            setRecording(false);
        });

        record.current.on('record-end', (blob) => {
            setRecording(false);
            setRecorded(true);

            const url = URL.createObjectURL(blob);
            setRecordedBlob(blob);
            setRecordedUrl(url);
            // onAudioUrlChanged(url);

            // Load the recorded audio into WaveSurfer
            if (wavesurfer.current) {
                wavesurfer.current.load(url);
            }
        });

        record.current.on('record-progress', (time: number) => {
            setDuration(
                convertSecondsToTimeWithSecond(Math.floor(time / 1000))
            );
        });

        wavesurfer.current.on('ready', () => {
            wavesurferReady.current = true;
        });

        wavesurfer.current.on('finish', () => {
            setIsPlaying(false);
        });
    };

    useEffect(() => {
        if (wavesurfer.current === undefined) {
            createRecorder();
        }

        return () => {
            if (wavesurfer.current && wavesurferReady.current) {
                wavesurfer.current.destroy();
            }
        };
    }, []);

    const recordClicked = () => {
        if (record.current?.isRecording() || record.current?.isPaused()) {
            record.current?.stopRecording();
            return;
        }

        record.current?.startRecording({});
    };

    const recordSubmit = () => {
        if (record.current?.isRecording() || record.current?.isPaused()) {
            record.current?.stopRecording();
        }
    };

    const recordCancel = () => {
        record.current?.destroy();
        setRecordedUrl(null);
        setRecordedBlob(null);
        setRecorded(false);
        const container = document.getElementById(id);
        if (container) {
            container.innerHTML = '';
        }

        createRecorder();
    };

    const handlePlayPause = () => {
        if (wavesurfer.current) {
            if (isPlaying) {
                wavesurfer.current.pause();
                setIsPlaying(false); // Update the UI
            } else {
                wavesurfer.current.play();
                setIsPlaying(true); // Update the UI
            }
        }
    };
    const getPlayPauseIcon = () => {
        return isPlaying ? (
            <div className="w-4 h-4 text-primary flex items-center justify-center">
                <StopIcon className="w-3 h-3 text-primary" />
            </div>
        ) : (
            <PlayIcon className="w-4 h-4" />
        );
    };

    useEffect(() => {
        if (isTextFetched) {
            setTimeout(() => {
                recordCancel();
            }, 300);
        }
    }, [isTextFetched]);
    return (
        <div className="space-x-2  self-end flex items-center ">
            <div
                className={` transition-opacity shadow-sm  bg-base-200 ms-auto gap-0.5 rounded-3xl flex items-center px-2 py-1 ${
                    recorded || recording ? 'opacity-100' : 'opacity-0'
                } `}
            >
                <div id={id} className="w-full hidden  " />

                <span className="text-primary-content text-xs">{duration}</span>
                {recorded && (
                    <>
                        <Button
                            size="smaller"
                            className="!rounded-full !p-0 !w-5 !h-5"
                            variant="ghost"
                            disabled={isFetchingText}
                            onClick={handlePlayPause}
                        >
                            {getPlayPauseIcon()}
                        </Button>
                        <Button
                            size="smaller"
                            className="!rounded-full !p-0 !w-5 !h-5"
                            variant="ghost"
                            isLoading={isFetchingText}
                            onClick={() =>
                                onAudioUrlChanged(
                                    recordedUrl as string,
                                    recordedBlob as Blob
                                )
                            }
                        >
                            {!isFetchingText && (
                                <SendIcon className="w-4 h-4" />
                            )}
                        </Button>
                    </>
                )}
            </div>
            <div className="rounded-full shadow-sm transition-transform duration-300">
                {isTextFetched && recordedUrl && recordedBlob && recorded ? (
                    <Button
                        className="rounded-full !bg-base-200 !w-7 !border-success 
                        !h-7"
                        variant="errorOutline"
                        size="circle"
                        onClick={() => {
                            recordCancel();
                        }}
                    >
                        <CheckIcon className=" h-4 w-4 text-success" />
                    </Button>
                ) : !recording ? (
                    <Button
                        className="!rounded-full !bg-base-200 !w-7
                        !h-7 "
                        variant="ghost"
                        size="circle"
                        disabled={isFetchingText}
                        onClick={recordClicked}
                    >
                        <MicrophoneIcon className="w-5 h-5" />
                    </Button>
                ) : (
                    <Button
                        className="rounded-full !bg-base-200 !w-7
                        !h-7"
                        variant="ghost"
                        size="circle"
                        onClick={recordSubmit}
                    >
                        <StopIcon className="mx-0.5 h-4 w-4 text-primary" />
                    </Button>
                )}
            </div>
        </div>
    );
}
